import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import '../styles/PapabileAdmin.css';

const PapabileAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cardinal, setCardinal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    papabile: false,
    raisonsForPapabile: [''],
    raisonsAgainstPapabile: ['']
  });
  const [imageError, setImageError] = useState(false);
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  // Charger les données du cardinal
  useEffect(() => {
    const fetchCardinal = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/cardinaux/${id}`);
        setCardinal(response.data);
        
        // Initialiser les données du formulaire
        setFormData({
          papabile: response.data.papabile || false,
          raisonsForPapabile: response.data.raisonsForPapabile && response.data.raisonsForPapabile.length > 0 
            ? [...response.data.raisonsForPapabile] 
            : [''],
          raisonsAgainstPapabile: response.data.raisonsAgainstPapabile && response.data.raisonsAgainstPapabile.length > 0
            ? [...response.data.raisonsAgainstPapabile]
            : ['']
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement du cardinal:', err);
        setError('Erreur lors du chargement du cardinal');
        setLoading(false);
      }
    };
    
    fetchCardinal();
  }, [id]);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl) => {
    if (!originalUrl || imageError) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = () => {
    console.log("Erreur de chargement d'image, utilisation de l'image par défaut");
    setImageError(true);
  };
  
  // Gérer le changement du statut papabile
  const handlePapabileChange = (e) => {
    const { checked } = e.target;
    setFormData(prev => ({
      ...prev,
      papabile: checked
    }));
  };
  
  // Ajouter un champ pour les raisons favorables
  const addForReason = () => {
    setFormData(prev => ({
      ...prev,
      raisonsForPapabile: [...prev.raisonsForPapabile, '']
    }));
  };
  
  // Supprimer un champ pour les raisons favorables
  const removeForReason = (index) => {
    setFormData(prev => ({
      ...prev,
      raisonsForPapabile: prev.raisonsForPapabile.filter((_, i) => i !== index)
    }));
  };
  
  // Gérer le changement d'une raison favorable
  const handleForReasonChange = (index, value) => {
    setFormData(prev => {
      const updatedReasons = [...prev.raisonsForPapabile];
      updatedReasons[index] = value;
      return {
        ...prev,
        raisonsForPapabile: updatedReasons
      };
    });
  };
  
  // Ajouter un champ pour les facteurs limitants
  const addAgainstReason = () => {
    setFormData(prev => ({
      ...prev,
      raisonsAgainstPapabile: [...prev.raisonsAgainstPapabile, '']
    }));
  };
  
  // Supprimer un champ pour les facteurs limitants
  const removeAgainstReason = (index) => {
    setFormData(prev => ({
      ...prev,
      raisonsAgainstPapabile: prev.raisonsAgainstPapabile.filter((_, i) => i !== index)
    }));
  };
  
  // Gérer le changement d'un facteur limitant
  const handleAgainstReasonChange = (index, value) => {
    setFormData(prev => {
      const updatedReasons = [...prev.raisonsAgainstPapabile];
      updatedReasons[index] = value;
      return {
        ...prev,
        raisonsAgainstPapabile: updatedReasons
      };
    });
  };
  
  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Filtrer les champs vides
      const cleanedFormData = {
        ...formData,
        raisonsForPapabile: formData.raisonsForPapabile.filter(reason => reason.trim() !== ''),
        raisonsAgainstPapabile: formData.raisonsAgainstPapabile.filter(reason => reason.trim() !== '')
      };
      
      // Si le cardinal n'est pas papabile, vider les raisons
      if (!cleanedFormData.papabile) {
        cleanedFormData.raisonsForPapabile = [];
        cleanedFormData.raisonsAgainstPapabile = [];
      }
      
      // Mettre à jour le cardinal
      await axios.put(`/api/cardinaux/${id}`, cleanedFormData);
      
      setSuccess('Les informations papabile ont été mises à jour avec succès');
      setSaving(false);
      
      // Rediriger après un délai
      setTimeout(() => {
        navigate(`/admin/cardinaux/edit/${id}`);
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du cardinal:', err);
      setError('Erreur lors de la mise à jour du cardinal');
      setSaving(false);
    }
  };
  
  if (loading) return <div className="loading">Chargement...</div>;
  if (!cardinal) return <div className="error">Cardinal non trouvé</div>;
  
  return (
    <div className="papabile-admin-page">
      <h1>Gestion du statut Papabile</h1>
      
      <div className="papabile-admin-header">
        <Link to={`/admin/cardinaux/edit/${id}`} className="back-button">
          ← Retour à l'édition du cardinal
        </Link>
        
        <div className="cardinal-preview">
          <div className="cardinal-preview-photo">
            <img 
              src={getApiImageUrl(cardinal.photo)} 
              alt={cardinal.nomComplet} 
              onError={handleImageError}
            />
          </div>
          <div className="cardinal-preview-info">
            <h2>{cardinal.nomComplet}</h2>
            <p>{cardinal.titre || cardinal.ordre}</p>
            <p>{cardinal.age} ans • {cardinal.paysService}</p>
            <p>
              <span className={`statut-badge ${cardinal.statutVotant === 'OUI' ? 'votant' : 'non-votant'}`}>
                {cardinal.statutVotant === 'OUI' ? 'Électeur' : 'Non-électeur'}
              </span>
              {cardinal.papabile && (
                <span className="statut-badge papabile">Papabile</span>
              )}
            </p>
          </div>
        </div>
      </div>
      
      {error && <div className="message error">{error}</div>}
      {success && <div className="message success">{success}</div>}
      
      <form onSubmit={handleSubmit} className="papabile-form">
        <div className="form-group checkbox">
          <label>
            <input 
              type="checkbox" 
              checked={formData.papabile} 
              onChange={handlePapabileChange}
            />
            Ce cardinal est considéré comme papabile
          </label>
        </div>
        
        {formData.papabile && (
          <div className="papabile-sections">
            <div className="papabile-section">
              <h3>Atouts comme papabile</h3>
              <p className="section-description">
                Quelles sont les caractéristiques qui font de ce cardinal un candidat potentiel au trône pontifical?
              </p>
              
              {formData.raisonsForPapabile.map((reason, index) => (
                <div key={`for-${index}`} className="reason-field">
                  <textarea
                    value={reason}
                    onChange={(e) => handleForReasonChange(index, e.target.value)}
                    placeholder="Atout comme papabile"
                    rows="2"
                  />
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeForReason(index)}
                    disabled={formData.raisonsForPapabile.length === 1}
                  >
                    ×
                  </button>
                </div>
              ))}
              
              <button 
                type="button" 
                className="add-button"
                onClick={addForReason}
              >
                + Ajouter un atout
              </button>
            </div>
            
            <div className="papabile-section">
              <h3>Facteurs limitants</h3>
              <p className="section-description">
                Quels facteurs pourraient limiter les chances de ce cardinal d'être élu pape?
              </p>
              
              {formData.raisonsAgainstPapabile.map((reason, index) => (
                <div key={`against-${index}`} className="reason-field">
                  <textarea
                    value={reason}
                    onChange={(e) => handleAgainstReasonChange(index, e.target.value)}
                    placeholder="Facteur limitant"
                    rows="2"
                  />
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeAgainstReason(index)}
                    disabled={formData.raisonsAgainstPapabile.length === 1}
                  >
                    ×
                  </button>
                </div>
              ))}
              
              <button 
                type="button" 
                className="add-button"
                onClick={addAgainstReason}
              >
                + Ajouter un facteur limitant
              </button>
            </div>
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
          <Link to={`/admin/cardinaux/edit/${id}`} className="cancel-button">
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
};

export default PapabileAdmin;